// @ts-nocheck
import React from 'react';
import { delay } from 'bluebird';
import SAlert from 'react-s-alert';
import redirect from './isomorphic-redirect';
import * as queryString from 'query-string';
import { pageWithTokenAuth } from '@scale/frontend-lib/auth';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { isPathNameIsExemptedFromRedirect } from '@scale/shared/types/growth/signup';
import serverFetch from 'isomorphic-fetch';

const isBrowser = typeof window !== 'undefined';
const MIN_RETRY = 200;
const andOrAmpersand = url => (url.indexOf('?') === -1 ? '?' : '&');

class FailedToFetchError extends Error {
  constructor(text) {
    super(text);
    this.failedToFetch = true;
  }
}

const getSpoof = context => {
  const query = parseQuery(context);
  let { spoof, spoof_env } = query;

  if (spoof) {
    spoof = spoof.split(' ').join('+');
  }

  return { spoof, spoof_env };
};

const getToken = context => {
  if (!context && typeof window === 'undefined') return;

  return (
    pageWithTokenAuth(context ? context.pathname : location.pathname) && parseQuery(context).token //  eslint-disable-line no-restricted-globals
  );
};

const parseQuery = context => {
  let query = {};
  if (context) {
    query = context.query;
  } else if (typeof window === 'object') {
    query = queryString.parse(queryString.extract(window.location.href));
  }
  return query;
};

/*
 * Fetch wrapper with auth handling, retry logic, cache invalidation and finger print.
 *
 * @param {Object} [context] - Next.js context for SSR requests.
 * @param {string} url - URL used in the request.
 * @param {Object} [options={}] - Options object to enable/disable features.
 * @param {number} [options.maxRetries=0] - Specify the number of max retries for failed requests.
 */
// eslint-disable-next-line complexity
export default function authFetch(context, url, options = {}) {
  options.headers = options.headers || {};

  const { spoof, spoof_env } = getSpoof(context);
  const token = getToken(context);

  // Get extra options and delete from the obj we pass to fetch
  const { noAuth, loginUrl, invalidateCache, fetchEventSourceOptions } = options;
  delete options.noAuth;
  delete options.noRedirect;
  delete options.loginUrl;
  delete options.invalidateCache;
  const maxRetries = options.maxRetries || 0;
  delete options.maxRetries;
  delete options.fetchEventSourceOptions;

  if (!noAuth) {
    if (token) {
      url += andOrAmpersand(url) + queryString.stringify({ token });
    }
    if (spoof && url.indexOf('spoof=') === -1) {
      url += andOrAmpersand(url) + queryString.stringify({ spoof });
    }
    if (spoof_env && url.indexOf('spoof_env=') === -1) {
      url += andOrAmpersand(url) + queryString.stringify({ spoof_env });
    }
  }

  if (invalidateCache) {
    options.cache = 'reload';
  }

  if (fetchEventSourceOptions) {
    const fetchSSEOptions = {
      ...options,
      ...fetchEventSourceOptions,
    };
    return fetchEventSource(url, fetchSSEOptions);
  }

  return (async () => {
    let retries = 0;
    for (;;) {
      let resp;
      try {
        // Use browser's native fetch when in browser environment, fallback to isomorphic-fetch for server
        if (isBrowser) {
          resp = await fetch(url, options);
        } else {
          resp = await serverFetch(url, options);
        }
      } catch (err) {
        // NOTE: We have two ways to react to request errors:
        // 1. If is a client side network error we will retry the request every 30s.
        // 2. If is not a network error we will retry the request every 0.2s-12s (with maxRetries 6).
        const isNetworkError =
          err.message &&
          [
            'failed to fetch', // chrome
            'networkerror when attempting to fetch resource.', // ff
            'not allowed to request resource', // safari
          ].includes(err.message.toLowerCase());
        if (isBrowser && isNetworkError && retries < maxRetries) {
          // Perhaps there's a problem with the cache
          options.cache = 'reload';
          await renderAlertAndWait30s();
          retries++;
          continue;
        }
        if (retries >= maxRetries) {
          throw new FailedToFetchError(err.message);
        }
      }

      // If we get a 401, we should immediately redirect the user to the login page to prevent
      // further loss of work.
      if (
        resp &&
        resp.status === 401 &&
        options.redirectOnUnauthorized &&
        !(context?.asPath && isPathNameIsExemptedFromRedirect(context.asPath))
      ) {
        return redirect(context, loginUrl || '/login', {
          includeCurrentURL: true,
          clear: true,
        });
      }

      if ((resp && resp.status < 500) || retries >= maxRetries) {
        return resp;
      }
      await delay(Math.pow(2, retries) * MIN_RETRY);
      retries++;
    }
  })();
}

async function renderAlertAndWait30s() {
  const retryTime = 30 * 1000; // 30 seconds
  SAlert.error(<Timer timer={30} />, { timeout: retryTime });
  await delay(retryTime);
}

class Timer extends React.Component {
  constructor(props) {
    super(props);
    this.state = { timer: this.props.timer };
  }

  componentDidMount() {
    this.interval = setInterval(() => this.tick(), 1000);
  }

  componentWillUnmount() {
    clearInterval(this.interval);
  }

  tick() {
    this.setState(prevState => ({
      timer: prevState.timer - 1,
    }));
  }

  render() {
    return (
      <div>
        Your computer seems to be offline. We&apos;ll try to reconnect in {this.state.timer}{' '}
        seconds.
      </div>
    );
  }
}
